import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./socialImpact.html";
import { PopoverHorizontalOrientation, PopoverVerticalOrientation } from 'wds/popover';
import { generateBadgeTooltips } from '~/modules/helpers/badgeHelper';
import { getListingPublisher, selectSocialImpactFeatures } from '~/modules/helpers/listingHelper';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { selectExtensionListing } from '~/modules/utilities/listing_utils';
const SOCIAL_IMPACT_IMAGE_URLS = {
  isPledge1Percent: '/images/listing/pledge.png',
  isDiverseOwnedBusiness: '/images/listing/diverse-owned.png'
};
/**
 * Social Impact section for the listing Overview tab. Renders one media card per
 * social-impact feature declared on the listing, built on the `wds-media-card`
 * primitive.
 */
class SocialImpact extends LightningElement {
  constructor(...args) {
    super(...args);
    this.listing = void 0;
    this.trackLearnMoreAsTile = false;
    this.i18n = void 0;
  }
  handleCardAnalytics(event) {
    if (this.trackLearnMoreAsTile && event.currentTarget.dataset.feature === 'isPledge1Percent' && event.detail.eventType === 'click' && event.detail.elementType === 'link') {
      event.detail.listingDetailCta = {
        elementType: 'tile',
        clickText: 'Learn More'
      };
    }
  }
  get features() {
    return selectSocialImpactFeatures(this.listing);
  }
  get providerName() {
    return getListingPublisher(this.listing)?.name ?? '';
  }
  get heading() {
    return i18nStore.getState().i18n.heading.providerImpact({
      PROVIDER_NAME: this.providerName
    });
  }

  /**
   * Per-feature tooltip for the "{Provider}'s Impact" heading. Renders as plain,
   * badge-free titled sections: a bold feature name over its body text, one
   * section per declared feature. The title reuses the existing badge label
   * (`listingDetails.badges.*`).
   *
   * Body text prefers the link-free override (`socialImpactTooltipsV2.*`),
   * falling back to the shared `tooltipsText.*`. Only Pledge 1% needs an override
   * because its shared copy carries a "Learn more" markdown link that must not
   * appear in the plain tooltip; Diverse-Owned's shared copy has no link, so it
   * reuses `tooltipsText` directly rather than duplicating the string.
   */
  get socialImpactTooltipSections() {
    const overrides = i18nStore.getState().i18nStaticCache?.listingDetails?.socialImpactTooltipsV2;
    // Shared badge tooltips supply the title (localized badge label) and the
    // default description text. Only Pledge 1% has an override, because its
    // shared copy carries a "Learn more" markdown link that must not appear in
    // the plain tooltip; Diverse-Owned's shared copy has no link, so it reuses
    // the shared text rather than duplicating the string.
    //
    // The override lookup is keyed explicitly per feature: `i18nStaticCache`
    // is a Proxy that returns the "[ERROR_I18N]" missing-text symbol (a truthy
    // string) for absent keys, so a generic `overrides[key]` would clobber the
    // shared description for every feature that lacks an override.
    const TOOLTIP_OVERRIDES = {
      isPledge1Percent: overrides?.isPledge1Percent
    };
    return generateBadgeTooltips(this.features).map(({
      badge,
      description
    }) => {
      // Read the key off the badge itself rather than re-deriving it by
      // positional index from `this.features` — that assumed
      // `generateBadgeTooltips` returns a same-length, same-order result,
      // an implicit coupling a future filter/reorder in the helper would
      // silently break. `generateLocalizedBadge` stamps `i18nKey` on every
      // badge, so it stays aligned with its own description.
      const key = badge.i18nKey;
      return {
        key,
        title: badge.i18nFn(),
        description: TOOLTIP_OVERRIDES[key] || description
      };
    });
  }

  // Show the heading tooltip whenever there are feature sections to describe.
  // The "{Provider}'s Impact" heading renders on both the v1 (`overview`) and
  // v2 (`overviewV2`) mounts, and the tooltip goes wherever that heading does.
  //
  // Gate on `features` (a cheap array of keys) rather than
  // `socialImpactTooltipSections`, which allocates badge objects and reads the
  // i18n store — this getter recomputes on every render, and both answer the
  // same "are there any features?" question.
  get showSocialImpactTooltip() {
    return this.features.length > 0;
  }

  // Center the popover (and its arrow) under the small info icon; the tooltip's
  // default RIGHT orientation assumes a wider trigger and leaves the arrow off to
  // the side of a bare icon. Mirrors the geographic-focus tooltip in overview.
  get socialImpactTooltipOrientation() {
    return {
      vertical: PopoverVerticalOrientation.BOTTOM,
      horizontal: PopoverHorizontalOrientation.CENTER
    };
  }
  get socialImpactCards() {
    const badges = this.i18n?.listingDetails?.badges;
    if (!badges) {
      return [];
    }
    const category = this.i18n.providerDetails.impact;
    const extension = selectExtensionListing(this.listing);
    return this.features.map(key => {
      const url = this.resolveCtaUrl(key, extension);
      const title = badges[key];
      // The diverse-owned CTA opens the business's certificate rather than a
      // generic "learn more" page.
      const isCertificate = key === 'isDiverseOwnedBusiness';
      const ctaLabel = isCertificate ? this.i18n.shared.viewCertificate : this.i18n.shared.learnMore;
      return {
        key,
        category,
        title,
        // Decorative image; empty alt text avoids repeating the adjacent title.
        image: {
          url: SOCIAL_IMPACT_IMAGE_URLS[key],
          altText: ''
        },
        callToAction: url ? {
          name: `social-impact-${key}`,
          label: ctaLabel,
          url
        } : null,
        // "Learn More" links share visible text, so give each a distinct accessible
        // name. "View Certificate" is already distinct and needs no override.
        ctaAriaLabel: url && !isCertificate ? i18nStore.getState().i18n.shared.learnMoreAbout({
          FEATURE: title
        }) : undefined,
        isExternalCta: true
      };
    });
  }
  resolveCtaUrl(key, extension) {
    if (key === 'isDiverseOwnedBusiness') {
      return extension?.diverseOwnedBusinessURL;
    }
    if (key === 'isPledge1Percent') {
      return this.i18n?.listingDetails?.pledge1PercentURL;
    }
    return undefined;
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(SocialImpact, {
  publicProps: {
    listing: {
      config: 0
    },
    trackLearnMoreAsTile: {
      config: 0
    }
  },
  wire: {
    i18n: {
      adapter: WireI18n,
      config: function ($cmp) {
        return {};
      }
    }
  }
});
const __lwc_component_class_internal = _registerComponent(SocialImpact, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;